import { inject, InjectionToken } from '@angular/core';
import {
  CanActivateFn,
  CanDeactivateFn,
  RedirectCommand,
  Router,
} from '@angular/router';
import { RouteUtilsService } from '@lassi/webapp-shared';

export const BOTTOM_SHEET_STORAGE = new InjectionToken<Storage>(
  'BOTTOM_SHEET_STORAGE',
);

export const clearSavedPath: CanDeactivateFn<never> = (_, currentRoute) => {
  const storage = inject(BOTTOM_SHEET_STORAGE);

  if (currentRoute.firstChild?.url?.length) {
    // this means we're closing the navigation from some inner page, in which case we let the saveCurrentPath save the path
    return true;
  }

  // otherwise delete the state
  const key = currentRoute.routeConfig?.path;

  storage.removeItem(
    RouteUtilsService.MAP_MENU_LAST_VISITED_SECTION_KEY + '.' + key,
  );
  storage.removeItem(
    RouteUtilsService.MAP_MENU_LAST_VISITED_SECTION_KEY +
      '.' +
      key +
      '.searchParams',
  );

  return true;
};

export const saveCurrentPath =
  (...params: string[]): CanDeactivateFn<never> =>
  (_, currentRoute, currentState) => {
    const storage = inject(BOTTOM_SHEET_STORAGE);

    const key = currentRoute.parent?.routeConfig?.path;
    const url = new URL(currentState.url, window.location.origin);

    storage.setItem(
      RouteUtilsService.MAP_MENU_LAST_VISITED_SECTION_KEY + '.' + key,
      url.pathname,
    );

    const save = new URLSearchParams();

    params.forEach((param) => {
      const value = url.searchParams.get(param);
      if (value) save.append(param, value);
    });

    storage.setItem(
      RouteUtilsService.MAP_MENU_LAST_VISITED_SECTION_KEY +
        '.' +
        key +
        '.searchParams',
      save.toString(),
    );

    return true;
  };

export const restoreCurrentPath: CanActivateFn = (route) => {
  const router = inject(Router);
  const storage = inject(BOTTOM_SHEET_STORAGE);

  const useRestore = (
    router.currentNavigation()?.extras?.info as { restore: boolean }
  )?.restore;
  if (!useRestore) {
    return true;
  }

  const key = route.parent?.routeConfig?.path;

  const storedPath = storage.getItem(
    RouteUtilsService.MAP_MENU_LAST_VISITED_SECTION_KEY + '.' + key,
  );

  const storedSearchParams = storage.getItem(
    RouteUtilsService.MAP_MENU_LAST_VISITED_SECTION_KEY +
      '.' +
      key +
      '.searchParams',
  );

  if (!storedPath) {
    return true;
  }

  const searchParams = new URLSearchParams(storedSearchParams ?? '');

  storage.removeItem(
    RouteUtilsService.MAP_MENU_LAST_VISITED_SECTION_KEY + '.' + key,
  );
  storage.removeItem(
    RouteUtilsService.MAP_MENU_LAST_VISITED_SECTION_KEY +
      '.' +
      key +
      '.searchParams',
  );

  const currentSearchParams = new URLSearchParams(route.queryParams);
  searchParams.forEach((value, key) => {
    currentSearchParams.set(key, value);
  });

  return new RedirectCommand(
    router.parseUrl(storedPath + '?' + currentSearchParams?.toString()),
    {
      replaceUrl: true,
    },
  );
};
